if (!customElements.get('product-gallery-slider')) {
  customElements.define('product-gallery-slider', class ProductGallerySlider extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('[id^="Slider-"]');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.currentPageElement = this.querySelector('.slider-counter--current');
      this.pageTotalElement = this.querySelector('.slider-counter--total');
      this.nav = this.querySelector('.slider-buttons');
      this.currentPage = 1;
      this.isProgrammaticScroll = false;
      this.lastSliderWidth = 0;
      this.onNavClick = this.onNavClick.bind(this);

      if (!this.slider) return;

      this.pruneSpiceFeat();
      this.resetPages();
      this.bindEvents();
    }

    connectedCallback() {
      requestAnimationFrame(() => this.pruneSpiceFeat());
    }

    bindEvents() {
      if (this.nav) this.nav.addEventListener('click', this.onNavClick, true);

      if (this.nav) {
        const navObserver = new MutationObserver(() => this.pruneSpiceFeat());
        navObserver.observe(this.nav, { childList: true, attributes: true, subtree: true, attributeFilter: ['style', 'class'] });
      }

      this.slider.addEventListener('scroll', () => {
        if (this.isProgrammaticScroll) return;
        this.syncFromScroll();
      }, { passive: true });

      const resizeObserver = new ResizeObserver(() => {
        const width = this.slider.clientWidth;
        if (!width || width === this.lastSliderWidth) return;
        this.lastSliderWidth = width;
        const index = this.currentPage - 1;
        this.resetPages();
        this.goToIndex(index, false);
      });
      resizeObserver.observe(this.slider);
    }

    pruneSpiceFeat() {
      if (!this.nav || this.pruning) return;
      this.pruning = true;
      const clones = this.nav.querySelectorAll('.spiceFeatPrev, .spiceFeatNext');
      clones.forEach((button) => button.remove());
      this.nav.querySelectorAll(':scope > .slider-button').forEach((button) => {
        if (button.style.display) button.style.removeProperty('display');
      });
      this.pruning = false;
      if (clones.length) this.updateButtonState();
    }

    visibleSlides() {
      return Array.from(this.slider.querySelectorAll(':scope > [id^="Slide-"]')).filter((slide) => {
        return !slide.classList.contains('is-variant-hidden');
      });
    }

    resetPages() {
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.sliderItemsToShow = this.visibleSlides();
      this.totalPages = Math.max(1, this.sliderItemsToShow.length);
      this.slidesPerPage = 1;
      this.sliderItemOffset = this.slider.clientWidth || 1;
      this.syncFromScroll();
    }

    currentIndex() {
      const slides = this.visibleSlides();
      if (!slides.length) return 0;
      const x = this.slider.scrollLeft;
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((slide, index) => {
        const dist = Math.abs(slide.offsetLeft - x);
        if (dist < bestDist) {
          bestDist = dist;
          best = index;
        }
      });
      return best;
    }

    goToIndex(index, smooth = true) {
      const slides = this.visibleSlides();
      if (!slides.length) return;
      const next = Math.max(0, Math.min(slides.length - 1, index));
      this.goToSlide(slides[next], smooth);
    }

    goToSlide(slide, smooth = true) {
      if (!slide || !this.slider) return;
      const slides = this.visibleSlides();
      this.isProgrammaticScroll = true;
      this.slider.scrollTop = 0;
      this.slider.scrollTo({
        left: slide.offsetLeft,
        behavior: smooth ? 'smooth' : 'auto'
      });
      this.setPage(slides.indexOf(slide) + 1, slide);
      clearTimeout(this.programmaticTimer);
      this.programmaticTimer = setTimeout(() => {
        this.isProgrammaticScroll = false;
      }, smooth ? 600 : 50);
    }

    setPage(page, element) {
      const slides = this.visibleSlides();
      const nextPage = Math.max(1, Math.min(this.totalPages || slides.length || 1, page));
      const currentElement = element || slides[nextPage - 1];
      const changed = this.currentPage !== nextPage;

      this.currentPage = nextPage;
      if (this.currentPageElement && this.currentPageElement.textContent !== String(this.currentPage)) {
        this.currentPageElement.textContent = this.currentPage;
      }
      if (this.pageTotalElement && this.pageTotalElement.textContent !== String(this.totalPages)) {
        this.pageTotalElement.textContent = this.totalPages;
      }

      this.updateButtonState();

      if (changed && currentElement) {
        this.dispatchEvent(new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement
          }
        }));
      }
    }

    updateButtonState() {
      if (!this.nav) return;
      const total = this.visibleSlides().length;
      this.totalPages = Math.max(1, total);
      const prev = this.nav.querySelector(':scope > .slider-button--prev:not(.spiceFeatPrev)');
      const next = this.nav.querySelector(':scope > .slider-button--next:not(.spiceFeatNext)');
      if (prev) {
        if (this.currentPage <= 1) prev.setAttribute('disabled', 'disabled');
        else prev.removeAttribute('disabled');
      }
      if (next) {
        if (total <= 1 || this.currentPage >= this.totalPages) next.setAttribute('disabled', 'disabled');
        else next.removeAttribute('disabled');
      }
    }

    syncFromScroll() {
      const slides = this.visibleSlides();
      if (!slides.length) return;
      this.totalPages = slides.length;
      this.setPage(this.currentIndex() + 1, slides[this.currentIndex()]);
    }

    onNavClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.nav.contains(button)) return;
      if (button.classList.contains('spiceFeatPrev') || button.classList.contains('spiceFeatNext')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (button.name !== 'previous' && button.name !== 'next') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const index = this.currentPage - 1;
      if (button.name === 'next') this.goToIndex(index + 1, true);
      else this.goToIndex(index - 1, true);
    }

    isSlideVisible(element, offset = 0) {
      if (!element || !this.slider) return false;
      const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
      return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
    }
  });
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');

      if (this.elements.viewer) {
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 200));
      }
      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
      }
    }

    connectedCallback() {
      requestAnimationFrame(() => {
        const active = this.querySelector('.product__media-item.is-active:not(.is-variant-hidden)')
          || this.querySelector('.product__media-item:not(.is-variant-hidden)');
        if (active && !this.mql.matches) this.playActiveMedia(active);
      });
    }

    onSlideChanged(event) {
      const current = event.detail.currentElement;
      if (!current) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      current.classList.add('is-active');
      this.playActiveMedia(current);

      if (!this.elements.thumbnails) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ current.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.mql.matches) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.resetPages) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        this.scrollToMedia(activeMedia);
        if (this.mql.matches && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    scrollToMedia(activeMedia) {
      if (!activeMedia) return;
      if (this.elements.viewer && typeof this.elements.viewer.goToSlide === 'function') {
        this.elements.viewer.goToSlide(activeMedia);
        return;
      }
      const slider = activeMedia.parentElement;
      if (!slider) return;
      slider.scrollTop = 0;
      slider.scrollTo({ left: activeMedia.offsetLeft });
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
